import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { AxiosError } from 'axios';
import { Bot, Info } from 'lucide-react';
import {
  LOGOUT_FAILED_MESSAGE,
  changeAgentClientCeiling,
  changeAgentClientRedirectUri,
  describeError,
  disableAgentClient,
  enableAgentClient,
  getMyOperatorAccess,
  listAgentAuthorizationIntents,
  listAgentClients,
  listAgentGrants,
  listAgentToolCalls,
  logoutPlatformSession,
  narrowAgentGrant,
  problemErrorCode,
  problemStatus,
  recertifyAgentClient,
  registerAgentClient,
  revokeAgentGrant,
  revokeAllWriteGrants,
  suspendAgentGrantWrites,
  type AgentActiveGrant,
  type AgentClient,
  type AgentGrant,
  type AgentManagementReason,
  type AgentToolCall,
  type AgentWriteScope,
  type AgentWriteScopeItem,
  type StepUpProof,
} from '@/lib/api';
import {
  AGENT_SCOPES,
  MANAGEMENT_REASONS,
  agentHighlights,
  ceilingScopes,
  classifyCeilingChange,
  clientStatusLabel,
  confirmsProfile,
  describeCeiling,
  describeScopes,
  writeScopesOf,
  grantStatusLabel,
  hasWriteScopes,
  isManagementReason,
  latestKnownCatalogVersion,
  reasonLabel,
  revokeReasonLabel,
  scopeLevel,
  scopesUpToLevel,
  validateDetail,
  DETAIL_MAX_LENGTH,
  REVOKE_ALL_WRITES_CONFIRMATION,
  SCOPE_DESCRIPTIONS,
} from '@/lib/agents';
import { isOperatorClient, isProfileClient } from '@/lib/operator-access';
import { useAuthStore } from '@/stores/auth';
import { Badge } from '@/components/ui';
import { OperatorAccessSection, type OperatorSettings } from '@/components/OperatorAccessSection';
import { toggleWriteScope } from '@/lib/agent-authorization';
import { stepUpTarget } from '@/lib/passkeys';
import { useStepUpDialog } from '@/lib/step-up';
import { PrepareAuthorizationPanel } from '@/components/PrepareAuthorizationPanel';
import { StepUpDialog } from '@/components/StepUpDialog';

/**
 * Página "Agentes de IA" (`/agentes`, só Admin; change platform-mcp-server, S-2.16).
 *
 * Gestão dos perfis do Hermes no MCP do backoffice: cliente OAuth por perfil, concessão ativa e
 * histórico, uso das ferramentas. Nenhum segredo passa por aqui: as rotas não devolvem token,
 * code nem hash, e a página não tem modal de credencial. Quem conecta ou renova é o Hermes, pelo
 * "Authenticate" da página de MCP dele; daqui só se registra, limita, corta e revoga.
 *
 * A API revalida tudo (conta Admin, sessão, login recente para ampliar, geração). A tela só
 * evita mandar o que a API recusaria e explica a recusa quando ela vem.
 *
 * "Preparar autorização" (change platform-mcp-writes, tarefa 1.18) abre, no perfil habilitado, o
 * painel da escrita que o próximo consentimento poderá oferecer (`PrepareAuthorizationPanel`).
 *
 * "Teto ampliado com espera" (mesma tarefa): no perfil do Hermes listado em `WriteProfiles` (o
 * `writeEligible` da lista de intenções, que é a mesma regra da rota de teto), "Mudar teto" oferece
 * também a escrita reservada. O corpo com escrita pede passkey presa ao cliente
 * (`agent_client.ceiling_write`); a subida com escrita volta 202 e fica pendente por 1 h, com aviso a
 * todos os Admins, e o cartão mostra o teto pendente e quando ele vale. Sem perfil de escrita
 * configurado, a tela não oferece escrita nenhuma.
 */

export const HERMES_INSTRUCTION = 'Para conectar ou renovar: Hermes → MCP → perfil → vaptapi → Authenticate';
export const UNAVAILABLE_MESSAGE = 'Recurso indisponível nesta versão da API.';
export const MISCONFIGURED_MESSAGE =
  'A configuração do MCP deste ambiente não está pronta; a gestão de agentes fica indisponível até ela ser concluída.';

const MISCONFIGURED_CODE = 'platform.agent_oauth.misconfigured';
const REAUTH_CODES = new Set([
  'platform.agent_client.recent_login_required',
  'platform.agent_client.mfa_session_required',
  'platform.agent_oauth.reauth_required',
]);
const GENERATION_MISMATCH_CODES = new Set([
  'platform.agent_client.generation_mismatch',
  'platform.agent_grant.generation_mismatch',
]);

type AgentFailure =
  | { kind: 'unavailable' }
  | { kind: 'misconfigured' }
  | { kind: 'reauth'; message: string }
  | { kind: 'generation_mismatch' }
  | { kind: 'forbidden'; message: string }
  | { kind: 'message'; message: string };

const ERROR_MESSAGES: Record<string, string> = {
  'platform.agent_client.profile_not_allowed':
    'Perfil não permitido neste ambiente. Use letras minúsculas, números e hífen (até 32, começando por letra), fora de "default" e "atendimento", e um perfil liberado na configuração do MCP.',
  'platform.agent_client.redirect_not_allowed':
    'URL de volta não liberada na configuração do MCP deste ambiente (a comparação é exata, caractere por caractere).',
  'platform.agent_client.invalid_detail': 'O detalhe aceita até 200 caracteres e não pode conter "vmp".',
  'platform.agent_management.account_not_allowed': 'Sua conta não tem acesso à gestão de agentes.',
  // As recusas próprias do cliente `Operator` ficam na OperatorAccessSection: os mesmos códigos no
  // registro de um perfil do Hermes seguem com o texto do servidor.
  // Teto com escrita (platform-mcp-writes, 1.18).
  'platform.agent_client.write_profile_not_allowed':
    'Este perfil não está entre os perfis de escrita deste ambiente: o teto dele só aceita leitura.',
  'platform.agent_client.full_control_not_allowed':
    'O controle total só vai ao teto de um perfil liberado para controle total, com o controle total ligado neste ambiente.',
  'platform.agent_client.ceiling_write_raise_not_alone':
    'A subida com escrita vai sozinha: mantenha a leitura atual e não tire outra escrita no mesmo pedido.',
  'platform.agent_client.not_enabled': 'O perfil não está habilitado. Recarregue a página e confira.',
};

function classifyAgentError(err: unknown): AgentFailure {
  const status = problemStatus(err);
  const code = problemErrorCode(err);
  if (status === 404 && !code) return { kind: 'unavailable' };
  if (status === 503 || code === MISCONFIGURED_CODE) return { kind: 'misconfigured' };
  if (status === 403) {
    const data = err instanceof AxiosError ? (err.response?.data as { reauthRequired?: unknown } | undefined) : undefined;
    if ((code && REAUTH_CODES.has(code)) || data?.reauthRequired === true) {
      return {
        kind: 'reauth',
        message:
          code === 'platform.agent_client.mfa_session_required'
            ? 'Esta ação exige uma sessão aberta com senha e TOTP (sem recovery code). Entre de novo e repita.'
            : 'Esta ação exige login nos últimos 15 minutos, com senha e TOTP. Entre de novo e repita.',
      };
    }
    return { kind: 'forbidden', message: (code && ERROR_MESSAGES[code]) || describeError(err) };
  }
  if (status === 409 && code && GENERATION_MISMATCH_CODES.has(code)) return { kind: 'generation_mismatch' };
  return { kind: 'message', message: (code && ERROR_MESSAGES[code]) || describeError(err) };
}

function formatDateTime(iso: string | null | undefined, empty = '—'): string {
  if (!iso) return empty;
  return new Date(iso).toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function hasPendingCeiling(client: AgentClient): boolean {
  return (client.pendingMaxScopes?.length ?? 0) > 0 && !!client.pendingMaxScopesEffectiveAt;
}

/**
 * O aviso depois do teto: a subida com escrita volta 202 com o pendente e a data em que vale; a
 * mudança que cancelou um pendente diz isso. Nulo: o aviso comum.
 */
function ceilingNotice(before: AgentClient, after: AgentClient | null | undefined): string | null {
  if (after && hasPendingCeiling(after)) {
    return (
      `Subida do teto do perfil ${before.profileName} agendada: ${describeCeiling(after.pendingMaxScopes ?? [])} ` +
      `vale a partir de ${formatDateTime(after.pendingMaxScopesEffectiveAt)}. Todos os Admins foram avisados; ` +
      'uma mudança de teto nova antes disso cancela a subida.'
    );
  }
  if (hasPendingCeiling(before)) {
    return `Mudar o teto do perfil: feito no perfil ${before.profileName}. A subida pendente foi cancelada.`;
  }
  return null;
}

type PendingAction =
  | { kind: 'revoke'; client: AgentClient; grant: AgentActiveGrant }
  | { kind: 'narrow'; client: AgentClient; grant: AgentActiveGrant }
  | { kind: 'suspend'; client: AgentClient; grant: AgentActiveGrant }
  | { kind: 'disable'; client: AgentClient }
  | { kind: 'enable'; client: AgentClient }
  | { kind: 'recertify'; client: AgentClient }
  | { kind: 'ceiling'; client: AgentClient }
  | { kind: 'redirect'; client: AgentClient };

type ActionKind = PendingAction['kind'];

interface ActionCopy {
  title: string;
  confirmLabel: string;
  danger: boolean;
  /** A rota grava o motivo e o detalhe? Nas outras, o motivo só confirma a intenção. */
  sendsReason: boolean;
  description: ReactNode;
}

const ACTION_COPY: Record<ActionKind, ActionCopy> = {
  revoke: {
    title: 'Revogar a concessão',
    confirmLabel: 'Revogar',
    danger: true,
    sendsReason: true,
    description:
      'A concessão sai de uso na hora, os pedidos de autorização ainda abertos deste perfil são negados e as propostas pendentes dela são canceladas na mesma transação. Para voltar, o Hermes precisa de um novo Authenticate.',
  },
  suspend: {
    title: 'Suspender escritas',
    confirmLabel: 'Suspender escritas',
    danger: true,
    sendsReason: false,
    description:
      'A concessão fica só com os escopos de leitura que já tinha, e a próxima escrita do agente é recusada. As propostas pendentes continuam aqui para decisão; para cancelá-las, revogue.',
  },
  narrow: {
    title: 'Reduzir a concessão',
    confirmLabel: 'Reduzir',
    danger: false,
    sendsReason: false,
    description: 'A concessão fica só com o nível escolhido, na requisição seguinte do agente. Reduzir não amplia nada.',
  },
  disable: {
    title: 'Desabilitar o perfil',
    confirmLabel: 'Desabilitar',
    danger: true,
    sendsReason: true,
    description:
      'Desabilitar o perfil corta o agente: a concessão ativa é revogada e a próxima chamada dele recebe 401. Habilitar de novo não revive a concessão.',
  },
  enable: {
    title: 'Habilitar o perfil',
    confirmLabel: 'Habilitar',
    danger: false,
    sendsReason: false,
    description:
      'Habilitar desfaz a contenção e exige login recente com senha e TOTP. O perfil volta sem concessão: o Hermes precisa de um novo Authenticate.',
  },
  recertify: {
    title: 'Recertificar o perfil',
    confirmLabel: 'Recertificar',
    danger: false,
    sendsReason: false,
    description:
      'Recertificar renova por mais um ciclo o direito do perfil a pedir consentimento. Exige login recente com senha e TOTP.',
  },
  ceiling: {
    title: 'Mudar o teto do perfil',
    confirmLabel: 'Mudar teto',
    danger: false,
    sendsReason: true,
    description:
      'Baixar o teto reduz a concessão ativa na mesma hora. Subir exige login recente e nunca amplia a concessão vigente: o nível novo só vale depois de um novo Authenticate.',
  },
  redirect: {
    title: 'Trocar a URL de volta',
    confirmLabel: 'Trocar URL',
    danger: false,
    sendsReason: true,
    description:
      'O perfil fica com o mesmo cliente OAuth e o mesmo histórico; o próximo Authenticate volta para a URL nova. A concessão vigente continua até ser substituída, e os pedidos de autorização ainda abertos são negados. Exige login recente com senha e TOTP.',
  },
};

interface LoadResult {
  tick: number;
  /** Relógio da leitura: os destaques são calculados contra ele, não contra cada render. */
  now: Date;
  clients?: AgentClient[];
  grants?: AgentGrant[];
  /**
   * O caminho do operador e o teto do ambiente (`GET /platform/me/operator-access`, tarefa 4.3);
   * nulo quando a leitura falha (API anterior à rota): a seção do operador volta a aprender o
   * caminho desligado pelo `409`.
   */
  operatorSettings?: OperatorSettings | null;
  failure?: AgentFailure;
}

export function AgentsPage() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [reloadTick, setReloadTick] = useState(0);
  const [loaded, setLoaded] = useState<LoadResult | null>(null);
  const [forcedUnavailable, setForcedUnavailable] = useState(false);
  const [pageNotice, setPageNotice] = useState<{ tone: 'info' | 'error' | 'warning'; text: string } | null>(null);
  const [reauthMessage, setReauthMessage] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [mutating, setMutating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [usageGrant, setUsageGrant] = useState<{ id: string; profileName: string } | null>(null);
  const [prepareFor, setPrepareFor] = useState<{ id: string; profileName: string } | null>(null);
  const [revokeAllOpen, setRevokeAllOpen] = useState(false);

  /** "Revogar todas as delegações de escrita" (change platform-mcp-writes, 1.22): contenção de incidente. */
  async function runRevokeAllWrites(input: { reason: AgentManagementReason; detail?: string }) {
    setMutating(true);
    setActionError(null);
    try {
      const result = await revokeAllWriteGrants(input);
      setRevokeAllOpen(false);
      setPageNotice({
        tone: 'info',
        text: `Delegações de escrita revogadas: ${result.revokedGrants}. Propostas pendentes canceladas: ${result.cancelledProposals}.`,
      });
      reload();
    } catch (err) {
      const failure = classifyAgentError(err);
      if (handleMutationFailure(failure)) {
        setRevokeAllOpen(false);
      } else {
        setActionError('message' in failure ? failure.message : describeError(err));
      }
    } finally {
      setMutating(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    // A leitura do caminho do operador não derruba a página: falhou, a seção segue sem ela.
    const operatorSettings = getMyOperatorAccess()
      .then(({ enabled, allowedScopes }): OperatorSettings => ({ enabled, allowedScopes }))
      .catch(() => null);
    Promise.all([listAgentClients(), listAgentGrants(), operatorSettings])
      .then(([clients, grants, settings]) => {
        if (!cancelled) setLoaded({ tick: reloadTick, now: new Date(), clients, grants, operatorSettings: settings });
      })
      .catch((err) => {
        if (cancelled) return;
        const failure = classifyAgentError(err);
        setLoaded({
          tick: reloadTick,
          now: new Date(),
          failure: failure.kind === 'generation_mismatch' ? { kind: 'message', message: describeError(err) } : failure,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  async function signInAgain() {
    try {
      await logoutPlatformSession();
    } catch {
      // Como o "Sair" do Shell: com a sessão viva no cookie, ir ao login fingiria que saiu, e
      // um carregamento sem login entraria de novo nela. Fica na página, com o motivo.
      setPageNotice({ tone: 'error', text: LOGOUT_FAILED_MESSAGE });
      return;
    }
    // Sem sessão, o ProtectedRoute leva ao login mesmo que o AdminOnlyRoute desvie antes para
    // /tenants.
    clearAuth();
    navigate('/login', { replace: true });
  }

  /** Recusa comum às mutações; devolve `true` quando tratou fora do modal. */
  function handleMutationFailure(failure: AgentFailure): boolean {
    switch (failure.kind) {
      case 'unavailable':
        setForcedUnavailable(true);
        return true;
      case 'misconfigured':
        setPageNotice({ tone: 'error', text: MISCONFIGURED_MESSAGE });
        return true;
      case 'reauth':
        setReauthMessage(failure.message);
        return true;
      case 'generation_mismatch':
        setPageNotice({
          tone: 'warning',
          text: 'O perfil mudou desde a leitura. A lista foi recarregada: confira e repita.',
        });
        reload();
        return true;
      default:
        return false;
    }
  }

  function handleActionError(err: unknown) {
    const failure = classifyAgentError(err);
    if (handleMutationFailure(failure)) {
      setPending(null);
    } else {
      setActionError('message' in failure ? failure.message : describeError(err));
    }
  }

  /** Chama a rota da ação e, no sucesso, fecha o modal e recarrega; o erro sobe a quem chamou. */
  async function performAction(action: PendingAction, input: ConfirmInput, proof?: StepUpProof) {
    const { client } = action;
    let notice = `${ACTION_COPY[action.kind].title}: feito no perfil ${client.profileName}.`;
    switch (action.kind) {
      case 'revoke':
        await revokeAgentGrant(action.grant.id, {
          expectedGeneration: action.grant.generation,
          reason: input.reason,
          detail: input.detail,
        });
        break;
      case 'narrow':
        await narrowAgentGrant(action.grant.id, {
          expectedGeneration: action.grant.generation,
          scopes: scopesUpToLevel(input.level ?? 0),
        });
        break;
      case 'suspend':
        await suspendAgentGrantWrites(action.grant.id, { expectedGeneration: action.grant.generation });
        break;
      case 'disable':
        await disableAgentClient(client.id, {
          expectedGeneration: client.generation,
          reason: input.reason,
          detail: input.detail,
        });
        break;
      case 'enable':
        await enableAgentClient(client.id, { expectedGeneration: client.generation });
        break;
      case 'recertify':
        await recertifyAgentClient(client.id, { expectedGeneration: client.generation });
        break;
      case 'ceiling': {
        const updated = await changeAgentClientCeiling(
          client.id,
          {
            expectedGeneration: client.generation,
            maxScopes: ceilingScopes(input.level ?? 0, input.writeScopes ?? []),
            reason: input.reason,
            detail: input.detail,
          },
          proof,
        );
        notice = ceilingNotice(client, updated) ?? notice;
        break;
      }
      case 'redirect':
        await changeAgentClientRedirectUri(client.id, {
          expectedGeneration: client.generation,
          redirectUri: input.redirectUri ?? '',
          reason: input.reason,
          detail: input.detail,
        });
        break;
    }
    setPending(null);
    setPageNotice({ tone: 'info', text: notice });
    reload();
  }

  async function runAction(action: PendingAction, input: ConfirmInput) {
    setMutating(true);
    setActionError(null);
    try {
      await performAction(action, input);
    } catch (err) {
      handleActionError(err);
    } finally {
      setMutating(false);
    }
  }

  // Teto com escrita (1.18): passkey presa ao cliente, com o corpo congelado ao abrir o modal. A
  // recusa do step-up deixa o modal aberto; as outras voltam ao modal de confirmação.
  const ceilingStepUp = useStepUpDialog<{ action: PendingAction; input: ConfirmInput }>(
    (proof, payload) => performAction(payload.action, payload.input, proof),
    handleActionError,
    { passkeyTarget: ({ action }) => stepUpTarget('agentClientCeilingWrite', action.client.id) },
  );

  function confirmAction(action: PendingAction, input: ConfirmInput) {
    if (action.kind === 'ceiling' && (input.writeScopes?.length ?? 0) > 0) {
      setActionError(null);
      ceilingStepUp.start({ action, input });
      return;
    }
    void runAction(action, input);
  }

  const loading = loaded?.tick !== reloadTick;
  const failure = forcedUnavailable ? ({ kind: 'unavailable' } as const) : loaded?.failure;
  const allClients = loaded?.clients ?? null;
  // Os perfis do Hermes seguem na lista de sempre; o cliente pessoal do operador (change
  // platform-mcp-operator-access) vai para a seção própria. Ausente `clientKind` é perfil.
  const clients = allClients ? allClients.filter(isProfileClient) : null;
  const operatorClients = allClients ? allClients.filter(isOperatorClient) : [];
  const grants = loaded?.grants ?? [];
  const now = loaded?.now ?? null;
  const environmentTag = allClients?.[0]?.environmentTag ?? null;
  const latestCatalog = clients ? latestKnownCatalogVersion(clients, grants) : null;

  const header = (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Bot size={18} />
        <div className="card-title">Agentes de IA do backoffice</div>
      </div>
      <p style={{ margin: 0 }} data-testid="hermes-instruction">
        <strong>{HERMES_INSTRUCTION}</strong>
      </p>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        <Info size={14} style={{ verticalAlign: 'text-bottom' }} /> O conector do claude.ai e do Claude Desktop não tem
        suporte (decisão O1): conectam ao MCP os perfis do Hermes e, quando o ambiente liga o caminho do operador, o
        Claude Code pessoal de um operador habilitado.
      </p>
    </div>
  );

  if (failure?.kind === 'unavailable') {
    return (
      <div className="page page-detail" style={{ gap: 16 }}>
        {header}
        <div className="info-banner" role="status">
          {UNAVAILABLE_MESSAGE}
        </div>
      </div>
    );
  }

  if (!loaded && loading) {
    return (
      <div className="page page-detail" style={{ gap: 16 }}>
        {header}
        <p className="muted">Carregando…</p>
      </div>
    );
  }

  return (
    <div className="page page-detail" style={{ gap: 16 }}>
      {header}

      {reauthMessage && (
        <div className="warning-banner" role="alert">
          {reauthMessage}{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void signInAgain()}>
            Entrar de novo
          </button>
        </div>
      )}

      {pageNotice && (
        <div
          className={
            pageNotice.tone === 'error' ? 'error-banner' : pageNotice.tone === 'warning' ? 'warning-banner' : 'info-banner'
          }
          role="status"
        >
          {pageNotice.text}
        </div>
      )}

      {failure?.kind === 'misconfigured' && (
        <div className="error-banner" role="alert">
          {MISCONFIGURED_MESSAGE}
        </div>
      )}
      {failure?.kind === 'reauth' && (
        <div className="warning-banner" role="alert">
          {failure.message}{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void signInAgain()}>
            Entrar de novo
          </button>
        </div>
      )}
      {(failure?.kind === 'forbidden' || failure?.kind === 'message') && (
        <div className="error-banner" role="alert">
          {failure.message}
        </div>
      )}
      {clients && now && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div className="muted" style={{ fontSize: 13 }}>
              {environmentTag ? (
                <>
                  Ambiente: <span className="mono">{environmentTag}</span> ·{' '}
                </>
              ) : null}
              {clients.length} {clients.length === 1 ? 'perfil' : 'perfis'}
              {loading ? ' · atualizando…' : ''}
            </div>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="btn btn-danger btn-sm"
              disabled={mutating}
              onClick={() => {
                setActionError(null);
                setRevokeAllOpen(true);
              }}
            >
              Revogar todas as delegações de escrita
            </button>
            {!registerOpen && (
              <button type="button" className="btn btn-sm" onClick={() => setRegisterOpen(true)}>
                Registrar perfil
              </button>
            )}
          </div>

          {registerOpen && (
            <RegisterAgentForm
              environmentTag={environmentTag}
              onCancel={() => setRegisterOpen(false)}
              onRegistered={(created) => {
                setRegisterOpen(false);
                setPageNotice({
                  tone: 'info',
                  text: `Perfil ${created.profileName} registrado. ${HERMES_INSTRUCTION}.`,
                });
                reload();
              }}
              onHandledFailure={handleMutationFailure}
            />
          )}

          {clients.length === 0 && !registerOpen && (
            <div className="card muted">Nenhum perfil registrado neste ambiente.</div>
          )}

          {clients.map((client) => (
            <AgentClientCard
              key={client.id}
              client={client}
              grants={grants.filter((g) => g.agentClientId === client.id)}
              now={now}
              latestCatalogVersion={latestCatalog}
              busy={mutating}
              onAction={(action) => {
                setActionError(null);
                setPending(action);
              }}
              onViewUsage={(grant) => setUsageGrant({ id: grant.id, profileName: client.profileName })}
              onPrepare={() => setPrepareFor({ id: client.id, profileName: client.profileName })}
            />
          ))}

          <OperatorAccessSection
            settings={loaded?.operatorSettings ?? null}
            clients={operatorClients}
            environmentTag={environmentTag}
            grants={grants}
            now={now}
            busy={mutating}
            onChanged={(text) => {
              setPageNotice({ tone: 'info', text });
              reload();
            }}
            onFailure={(err) => {
              const failure = classifyAgentError(err);
              if (handleMutationFailure(failure)) return null;
              return 'message' in failure ? failure.message : describeError(err);
            }}
          />
          {prepareFor && (
            <PrepareAuthorizationPanel
              key={prepareFor.id}
              clientId={prepareFor.id}
              profileName={prepareFor.profileName}
              onClose={() => setPrepareFor(null)}
            />
          )}

          {usageGrant && (
            <ToolCallsPanel
              key={usageGrant.id}
              grantId={usageGrant.id}
              profileName={usageGrant.profileName}
              onClose={() => setUsageGrant(null)}
            />
          )}
        </>
      )}

      {pending && (
        <ConfirmActionModal
          action={pending}
          submitting={mutating || ceilingStepUp.open || ceilingStepUp.busy}
          error={actionError}
          onCancel={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={(input) => confirmAction(pending, input)}
        />
      )}

      {ceilingStepUp.open && pending?.kind === 'ceiling' && (
        <StepUpDialog
          title={`Confirmar o teto com escrita do perfil ${pending.client.profileName}`}
          description={
            <p style={{ margin: 0 }}>
              Teto com escopo de escrita pede confirmação por passkey, presa a este perfil. A subida com escrita fica
              pendente por 1 h e avisa todos os Admins.
            </p>
          }
          confirmLabel="Mudar teto"
          busy={ceilingStepUp.busy}
          error={ceilingStepUp.error}
          onSubmit={ceilingStepUp.submit}
          onCancel={ceilingStepUp.cancel}
          onPasskey={ceilingStepUp.passkeyAvailable ? ceilingStepUp.submitPasskey : undefined}
          passkeyOnly
        />
      )}

      {revokeAllOpen && (
        <RevokeAllWritesModal
          submitting={mutating}
          error={actionError}
          onCancel={() => {
            setRevokeAllOpen(false);
            setActionError(null);
          }}
          onConfirm={(input) => void runRevokeAllWrites(input)}
        />
      )}
    </div>
  );
}

/**
 * Confirmação de "Revogar todas as delegações de escrita": motivo da lista fechada, detalhe
 * opcional e o texto fixo digitado. A API revoga, numa transação, toda concessão ativa com escopo
 * de escrita deste ambiente e cancela as propostas pendentes; as concessões só de leitura ficam.
 */
function RevokeAllWritesModal({
  submitting,
  error,
  onCancel,
  onConfirm,
}: {
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (input: { reason: AgentManagementReason; detail?: string }) => void;
}) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const detailError = validateDetail(detail);
  const canConfirm =
    typed === REVOKE_ALL_WRITES_CONFIRMATION && isManagementReason(reason) && detailError === null && !submitting;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canConfirm || !isManagementReason(reason)) return;
    onConfirm({ reason, detail });
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="revoke-all-writes-title"
        onSubmit={submit}
        style={{ background: 'var(--color-surface)', borderRadius: 12, padding: 24, maxWidth: 480, width: '90%' }}
      >
        <h2 id="revoke-all-writes-title" style={{ marginTop: 0, fontSize: 18 }}>
          Revogar todas as delegações de escrita
        </h2>
        <p style={{ fontSize: 13 }}>
          Toda concessão ativa com escopo de escrita deste ambiente sai de uso na hora, e as propostas pendentes
          dos agentes são canceladas na mesma transação. As concessões só de leitura continuam. Para voltar, cada
          perfil precisa de nova autorização.
        </p>
        <div className="field">
          <label htmlFor="revoke-all-writes-typed">
            Digite <span className="mono">{REVOKE_ALL_WRITES_CONFIRMATION}</span> para confirmar
          </label>
          <input
            id="revoke-all-writes-typed"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label htmlFor="revoke-all-writes-reason">Motivo</label>
          <select id="revoke-all-writes-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Escolha o motivo</option>
            {MANAGEMENT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="revoke-all-writes-detail">Detalhe (opcional, até {DETAIL_MAX_LENGTH} caracteres)</label>
          <textarea
            id="revoke-all-writes-detail"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            rows={2}
            aria-invalid={detailError !== null}
          />
          {detailError && (
            <div className="muted" role="alert" style={{ fontSize: 12, color: 'var(--color-error)' }}>
              {detailError}
            </div>
          )}
        </div>
        {error && (
          <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-danger" disabled={!canConfirm}>
            {submitting ? 'Revogando…' : 'Revogar todas'}
          </button>
        </div>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Cartão de um perfil
// -----------------------------------------------------------------------------

function AgentClientCard({
  client,
  grants,
  now,
  latestCatalogVersion,
  busy,
  onAction,
  onViewUsage,
  onPrepare,
}: {
  client: AgentClient;
  grants: AgentGrant[];
  now: Date;
  latestCatalogVersion: number | null;
  busy: boolean;
  onAction: (action: PendingAction) => void;
  onViewUsage: (grant: { id: string }) => void;
  onPrepare: () => void;
}) {
  const enabled = client.status === 'Enabled';
  const grant = client.activeGrant;
  const highlights = agentHighlights(client, now, latestCatalogVersion);
  const history = grants.filter((g) => g.id !== grant?.id);
  // Propostas pendentes da concessão ativa (change platform-mcp-writes, 1.22), da listagem de concessões.
  const pendingProposals = grants.find((g) => g.id === grant?.id)?.pendingProposals ?? 0;

  return (
    <section className="card" aria-label={`Perfil ${client.profileName}`} data-testid={`agent-client-${client.profileName}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div className="card-title mono">{client.profileName}</div>
        <Badge tone={enabled ? 'success' : 'neutral'} dot>
          {clientStatusLabel(client.status)}
        </Badge>
        {highlights.map((h) => (
          <Badge key={h.kind} tone={h.kind === 'catalog_ahead' ? 'info' : 'warning'}>
            <span data-testid={`highlight-${h.kind}`}>{h.label}</span>
          </Badge>
        ))}
        {hasPendingCeiling(client) && <Badge tone="warning">subida de teto pendente</Badge>}
      </div>

      {(highlights.some((h) => h.kind === 'no_grant' || h.kind === 'expiring')) && (
        <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
          {HERMES_INSTRUCTION}
        </p>
      )}

      <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', margin: 0, fontSize: 13 }}>
        <dt className="muted">Ambiente</dt>
        <dd style={{ margin: 0 }} className="mono">{client.environmentTag}</dd>
        <dt className="muted">Cliente OAuth</dt>
        <dd style={{ margin: 0 }} className="mono">{client.oauthClientId}</dd>
        <dt className="muted">Host de volta</dt>
        <dd style={{ margin: 0 }} className="mono">{client.redirectHost}</dd>
        <dt className="muted">Teto</dt>
        <dd style={{ margin: 0 }}>{describeCeiling(client.maxScopes)}</dd>
        {hasPendingCeiling(client) && (
          <>
            <dt className="muted">Teto pendente</dt>
            <dd style={{ margin: 0 }} data-testid="pending-ceiling">
              {describeCeiling(client.pendingMaxScopes ?? [])} · vale a partir de{' '}
              {formatDateTime(client.pendingMaxScopesEffectiveAt)} · uma mudança de teto nova cancela esta subida
            </dd>
          </>
        )}
        <dt className="muted">Recertificação</dt>
        <dd style={{ margin: 0 }}>até {formatDateTime(client.certificationDueAt)}</dd>
        {!enabled && (
          <>
            <dt className="muted">Desabilitado</dt>
            <dd style={{ margin: 0 }}>
              {formatDateTime(client.disabledAt)} · {reasonLabel(client.disabledReason)}
            </dd>
          </>
        )}
        <dt className="muted">Concessão ativa</dt>
        <dd style={{ margin: 0 }}>
          {grant ? (
            <>
              {describeScopes(grant.effectiveScopes)} · aprovada por {grant.approvedByName ?? 'conta removida'} · vence{' '}
              {formatDateTime(grant.expiresAt)} · último uso {formatDateTime(grant.lastUsedAt, 'nunca')} · catálogo v
              {grant.catalogVersion}
            </>
          ) : (
            'nenhuma'
          )}
        </dd>
      </dl>

      <div className="btn-row" style={{ marginTop: 12 }}>
        {enabled && (
          <>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onAction({ kind: 'ceiling', client })}>
              Mudar teto
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onAction({ kind: 'recertify', client })}>
              Recertificar
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={onPrepare}>
              Preparar autorização
            </button>
            {grant && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy || scopeLevel(grant.scopes) <= 1}
                  title={scopeLevel(grant.scopes) <= 1 ? 'A concessão já está no nível mais baixo' : undefined}
                  onClick={() => onAction({ kind: 'narrow', client, grant })}
                >
                  Reduzir
                </button>
                {hasWriteScopes(grant.scopes) && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    disabled={busy}
                    onClick={() => onAction({ kind: 'suspend', client, grant })}
                  >
                    Suspender escritas
                  </button>
                )}
                <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => onAction({ kind: 'revoke', client, grant })}>
                  {hasWriteScopes(grant.scopes) || pendingProposals > 0
                    ? `Revogar e cancelar pendentes (${pendingProposals})`
                    : 'Revogar concessão'}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onViewUsage(grant)}>
                  Ver uso
                </button>
              </>
            )}
            <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => onAction({ kind: 'disable', client })}>
              Desabilitar
            </button>
          </>
        )}
        {!enabled && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onAction({ kind: 'enable', client })}>
            Habilitar
          </button>
        )}
        {/* Também no desabilitado: depois de um incidente que tirou o host antigo da configuração,
            é a troca da URL que deixa o perfil habilitável de novo. */}
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onAction({ kind: 'redirect', client })}>
          Trocar URL de volta
        </button>
      </div>

      {history.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Histórico de concessões ({history.length})</summary>
          <div className="table-scroll">
            <table aria-label={`Histórico de concessões do perfil ${client.profileName}`}>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Escopos</th>
                  <th>Aprovada por</th>
                  <th>Emitida</th>
                  <th>Saiu de uso</th>
                  <th>Motivo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.map((g) => (
                  <tr key={g.id}>
                    <td>{grantStatusLabel(g.status)}</td>
                    <td>{describeScopes(g.scopes)}</td>
                    <td>{g.approvedByName ?? '—'}</td>
                    <td>{formatDateTime(g.issuedAt)}</td>
                    <td>{formatDateTime(g.revokedAt)}</td>
                    <td>
                      {revokeReasonLabel(g.revokeReason)}
                      {g.managementReason ? ` · ${reasonLabel(g.managementReason)}` : ''}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        aria-label="Ver uso desta concessão"
                        onClick={() => onViewUsage(g)}
                      >
                        Ver uso
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
//  Confirmação embutida (molde do ReasonModal da OperatorDetailPage)
// -----------------------------------------------------------------------------

interface ConfirmInput {
  reason: AgentManagementReason;
  detail?: string;
  level?: number;
  redirectUri?: string;
  /** Teto: a escrita marcada (1.18). Com alguma, a confirmação pede passkey. */
  writeScopes?: AgentWriteScope[];
}

/**
 * `includeCurrent`: no teto com escrita oferecida ou com subida pendente, o nível atual também é
 * escolha (subir só a escrita, ou repetir o teto atual para cancelar a subida).
 */
function levelOptions(action: PendingAction, includeCurrent = false): number[] {
  if (action.kind === 'ceiling') {
    const current = scopeLevel(action.client.maxScopes);
    return [1, 2, 3].filter((level) => includeCurrent || level !== current);
  }
  if (action.kind === 'narrow') {
    const current = scopeLevel(action.grant.scopes);
    return [1, 2, 3].filter((level) => level < current);
  }
  return [];
}

function ConfirmActionModal({
  action,
  submitting,
  error,
  onCancel,
  onConfirm,
}: {
  action: PendingAction;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (input: ConfirmInput) => void;
}) {
  const copy = ACTION_COPY[action.kind];
  const { client } = action;
  const [typedName, setTypedName] = useState('');
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [level, setLevel] = useState<number | null>(null);
  const [redirectUri, setRedirectUri] = useState('');

  // Teto com escrita (1.18): só no perfil do Hermes, e só com a escrita que a API aceitaria para
  // ele (o `writeEligible` e os `availableScopes` da lista de intenções, a mesma regra da rota de
  // teto). Falha na leitura: nada de escrita oferecida, como no perfil de leitura.
  const isCeiling = action.kind === 'ceiling';
  const currentWrite = isCeiling ? writeScopesOf(client.maxScopes) : [];
  const [writeOffer, setWriteOffer] = useState<AgentWriteScopeItem[]>([]);
  const [writeSelected, setWriteSelected] = useState<AgentWriteScope[]>(currentWrite);
  const pendingCeiling = isCeiling && hasPendingCeiling(client);
  useEffect(() => {
    if (action.kind !== 'ceiling' || !isProfileClient(action.client)) return;
    let cancelled = false;
    listAgentAuthorizationIntents(action.client.id)
      .then((list) => {
        if (!cancelled && list?.writeEligible) setWriteOffer(list.availableScopes ?? []);
      })
      .catch(() => {
        // Sem a lista, o teto segue só com a leitura (e a escrita que já houver).
      });
    return () => {
      cancelled = true;
    };
  }, [action]);
  // A escrita listada: a oferecida para o perfil e a que o teto já tem (para poder tirá-la).
  const writeItems: AgentWriteScopeItem[] = [
    ...writeOffer,
    ...currentWrite.filter((s) => !writeOffer.some((o) => o.scope === s)).map((scope) => ({ scope, label: scope })),
  ];
  const writeAllowed = writeItems.map((item) => item.scope);
  const offersWrite = writeItems.length > 0;
  const currentLevel = scopeLevel(client.maxScopes);
  const options = levelOptions(action, offersWrite || pendingCeiling);
  // Com escrita na tela, o nível atual vem marcado: subir só a escrita é o caso comum.
  const chosenLevel = level ?? (isCeiling && offersWrite ? currentLevel : null);
  const requested = isCeiling && chosenLevel !== null ? ceilingScopes(chosenLevel, writeSelected) : [];
  const ceilingChange = isCeiling && chosenLevel !== null ? classifyCeilingChange(client.maxScopes, requested) : null;
  const ceilingReady =
    !isCeiling || (ceilingChange !== null && ceilingChange !== 'mixed' && (ceilingChange !== 'unchanged' || pendingCeiling));

  const detailError = copy.sendsReason ? validateDetail(detail) : null;
  const needsLevel = action.kind === 'ceiling' || action.kind === 'narrow';
  const needsRedirect = action.kind === 'redirect';
  // A API confere a URL contra a configuração do ambiente; aqui só se evita mandar vazia ou igual.
  const redirectReady = redirectUri.length > 0 && redirectUri !== client.redirectUri;
  const canConfirm =
    confirmsProfile(typedName, client.profileName) &&
    isManagementReason(reason) &&
    detailError === null &&
    (!needsLevel || (chosenLevel !== null && options.includes(chosenLevel))) &&
    ceilingReady &&
    (!needsRedirect || redirectReady) &&
    !submitting;

  const lastUsedAt =
    action.kind === 'revoke' || action.kind === 'narrow' || action.kind === 'suspend'
      ? action.grant.lastUsedAt
      : client.activeGrant?.lastUsedAt;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canConfirm || !isManagementReason(reason)) return;
    onConfirm({
      reason,
      detail: copy.sendsReason ? detail : undefined,
      level: needsLevel ? (chosenLevel ?? undefined) : undefined,
      redirectUri: needsRedirect ? redirectUri : undefined,
      writeScopes: isCeiling ? writeScopesOf(requested) : undefined,
    });
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 40,
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-action-title"
        onSubmit={submit}
        style={{
          background: 'var(--color-surface)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h2 id="agent-action-title" style={{ marginTop: 0, fontSize: 18 }}>
          {copy.title}
        </h2>
        <p style={{ fontSize: 13 }}>{copy.description}</p>
        <dl
          data-testid="agent-action-context"
          style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', fontSize: 13 }}
        >
          <dt className="muted">Perfil</dt>
          <dd style={{ margin: 0 }} className="mono">{client.profileName}</dd>
          <dt className="muted">Ambiente</dt>
          <dd style={{ margin: 0 }} className="mono">{client.environmentTag}</dd>
          <dt className="muted">Último uso</dt>
          <dd style={{ margin: 0 }}>{formatDateTime(lastUsedAt, 'nunca')}</dd>
          {needsRedirect && (
            <>
              <dt className="muted">URL de volta atual</dt>
              <dd style={{ margin: 0, wordBreak: 'break-all' }} className="mono">
                {client.redirectUri}
              </dd>
            </>
          )}
        </dl>

        {needsRedirect && (
          <div className="field">
            <label htmlFor="agent-action-redirect">Nova URL de volta (redirect_uri do perfil no Hermes)</label>
            <input
              id="agent-action-redirect"
              value={redirectUri}
              onChange={(e) => setRedirectUri(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              inputMode="url"
            />
          </div>
        )}

        {needsLevel && (
          <div className="field">
            <label htmlFor="agent-action-level">{action.kind === 'ceiling' ? 'Novo teto' : 'Reduzir para'}</label>
            <select
              id="agent-action-level"
              value={chosenLevel ?? ''}
              onChange={(e) => setLevel(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Escolha o nível</option>
              {options.map((option) => (
                <option key={option} value={option}>
                  {describeScopes(scopesUpToLevel(option))}
                  {isCeiling && option === currentLevel ? ' (atual)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {isCeiling && pendingCeiling && (
          <div className="warning-banner" role="note" data-testid="ceiling-pending-notice" style={{ marginBottom: 12 }}>
            Subida pendente: {describeCeiling(client.pendingMaxScopes ?? [])}, vale a partir de{' '}
            {formatDateTime(client.pendingMaxScopesEffectiveAt)}. Qualquer outra mudança de teto, inclusive repetir o teto
            atual, a cancela; repetir a mesma subida mantém a espera.
          </div>
        )}

        {isCeiling && offersWrite && (
          <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }} disabled={submitting}>
            <legend style={{ fontWeight: 600, marginBottom: 6 }}>Escrita no teto (perfil de escrita)</legend>
            {writeItems.map((item) => (
              <label key={item.scope} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={writeSelected.includes(item.scope)}
                  onChange={(e) => setWriteSelected(toggleWriteScope(writeSelected, item.scope, e.target.checked, writeAllowed))}
                />
                <span>
                  <span className="mono">{item.scope}</span>
                  {item.label !== item.scope ? ` — ${item.label}` : ''}
                </span>
              </label>
            ))}
          </fieldset>
        )}

        {ceilingChange === 'write_raise' && (
          <div className="warning-banner" role="note" data-testid="ceiling-write-wait" style={{ marginBottom: 12 }}>
            A subida com escrita não vale já: fica pendente por 1 h e avisa todos os Admins. A confirmação pede passkey.
            O teto atual continua valendo até lá.
          </div>
        )}
        {ceilingChange === 'mixed' && (
          <div className="error-banner" role="alert" data-testid="ceiling-write-mixed" style={{ marginBottom: 12 }}>
            A subida com escrita vai sozinha: mantenha a leitura atual e não tire outra escrita no mesmo pedido.
          </div>
        )}
        {isCeiling && ceilingChange !== 'write_raise' && ceilingChange !== 'mixed' && writeSelected.length > 0 && (
          <p className="muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
            Teto com escrita: a confirmação pede passkey.
          </p>
        )}

        <div className="field">
          <label htmlFor="agent-action-profile">
            Digite o nome do perfil (<span className="mono">{client.profileName}</span>) para confirmar
          </label>
          <input
            id="agent-action-profile"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="field">
          <label htmlFor="agent-action-reason">Motivo</label>
          <select id="agent-action-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Escolha o motivo</option>
            {MANAGEMENT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {!copy.sendsReason && (
            <div className="muted" style={{ fontSize: 12 }}>
              Esta rota não grava o motivo; ele só confirma a intenção.
            </div>
          )}
        </div>

        {copy.sendsReason && (
          <div className="field">
            <label htmlFor="agent-action-detail">Detalhe (opcional, até {DETAIL_MAX_LENGTH} caracteres)</label>
            <textarea
              id="agent-action-detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={2}
              aria-invalid={detailError !== null}
            />
            {detailError && (
              <div className="muted" role="alert" style={{ fontSize: 12, color: 'var(--color-error)' }}>
                {detailError}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" className={copy.danger ? 'btn btn-danger' : 'btn'} disabled={!canConfirm}>
            {submitting ? 'Confirmando…' : copy.confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------------------
//  Registro de perfil
// -----------------------------------------------------------------------------

function RegisterAgentForm({
  environmentTag,
  onCancel,
  onRegistered,
  onHandledFailure,
}: {
  environmentTag: string | null;
  onCancel: () => void;
  onRegistered: (client: AgentClient) => void;
  onHandledFailure: (failure: AgentFailure) => boolean;
}) {
  const [profileName, setProfileName] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [level, setLevel] = useState(1);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detailError = validateDetail(detail);
  const canSubmit =
    profileName.length > 0 && redirectUri.length > 0 && isManagementReason(reason) && detailError === null && !submitting;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit || !isManagementReason(reason)) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await registerAgentClient({
        profileName,
        redirectUri,
        maxScopes: scopesUpToLevel(level),
        reason,
        detail,
      });
      onRegistered(created);
    } catch (err) {
      const failure = classifyAgentError(err);
      if (!onHandledFailure(failure)) {
        setError('message' in failure ? failure.message : describeError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} aria-label="Registrar perfil do Hermes">
      <div className="card-title" style={{ marginBottom: 6 }}>
        Registrar perfil do Hermes
      </div>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        O id do cliente é derivado pelo servidor (hermes-{environmentTag ?? '<ambiente>'}-perfil). Registrar exige login
        recente com senha e TOTP. Depois, conecte pelo Hermes.
      </p>
      <div className="grid-halves">
        <div className="field">
          <label htmlFor="agent-register-profile">Perfil</label>
          <input
            id="agent-register-profile"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            maxLength={32}
          />
        </div>
        <div className="field">
          <label htmlFor="agent-register-redirect">URL de volta (redirect_uri do perfil no Hermes)</label>
          <input
            id="agent-register-redirect"
            value={redirectUri}
            onChange={(e) => setRedirectUri(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            inputMode="url"
          />
        </div>
        <div className="field">
          <label htmlFor="agent-register-level">Teto</label>
          <select id="agent-register-level" value={level} onChange={(e) => setLevel(Number(e.target.value))}>
            {AGENT_SCOPES.map((scope, index) => (
              <option key={scope} value={index + 1} title={SCOPE_DESCRIPTIONS[scope]}>
                {describeScopes(scopesUpToLevel(index + 1))}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="agent-register-reason">Motivo</label>
          <select id="agent-register-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Escolha o motivo</option>
            {MANAGEMENT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="agent-register-detail">Detalhe (opcional, até {DETAIL_MAX_LENGTH} caracteres)</label>
        <textarea
          id="agent-register-detail"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={2}
          aria-invalid={detailError !== null}
        />
        {detailError && (
          <div role="alert" style={{ fontSize: 12, color: 'var(--color-error)' }}>
            {detailError}
          </div>
        )}
      </div>
      {error && (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}
      <div className="btn-row">
        <button type="submit" className="btn" disabled={!canSubmit}>
          {submitting ? 'Registrando…' : 'Registrar'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// -----------------------------------------------------------------------------
//  Uso de uma concessão
// -----------------------------------------------------------------------------

function ToolCallsPanel({
  grantId,
  profileName,
  onClose,
}: {
  grantId: string;
  profileName: string;
  onClose: () => void;
}) {
  const [days, setDays] = useState(7);
  const [items, setItems] = useState<AgentToolCall[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const key = `${grantId}:${days}`;

  useEffect(() => {
    let cancelled = false;
    listAgentToolCalls(grantId, { days })
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setNextCursor(page.nextCursor);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        const failure = classifyAgentError(err);
        setItems([]);
        setNextCursor(null);
        setError(
          failure.kind === 'unavailable'
            ? UNAVAILABLE_MESSAGE
            : failure.kind === 'misconfigured'
              ? MISCONFIGURED_MESSAGE
              : 'message' in failure
                ? failure.message
                : describeError(err),
        );
      })
      .finally(() => {
        if (!cancelled) setLoadedKey(`${grantId}:${days}`);
      });
    return () => {
      cancelled = true;
    };
  }, [grantId, days]);

  const loading = loadedKey !== key;

  // Cada troca de janela abre uma geração nova: o "Carregar mais" disparado na janela anterior
  // é descartado ao voltar, para não anexar chamadas de uma janela à lista da outra nem gravar
  // o cursor dela. O painel é remontado quando a concessão muda (key no pai), então só a janela
  // precisa disso.
  const windowGeneration = useRef(0);

  function changeWindow(next: number) {
    windowGeneration.current += 1;
    setLoadingMore(false);
    setNextCursor(null);
    setDays(next);
  }

  async function loadMore() {
    if (!nextCursor || loading) return;
    const generation = windowGeneration.current;
    setLoadingMore(true);
    try {
      const page = await listAgentToolCalls(grantId, { days, cursor: nextCursor });
      if (generation !== windowGeneration.current) return;
      setItems((current) => [...current, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (generation !== windowGeneration.current) return;
      setError(describeError(err));
    } finally {
      if (generation === windowGeneration.current) setLoadingMore(false);
    }
  }

  return (
    <section className="card" aria-label={`Uso da concessão do perfil ${profileName}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div className="card-title">
          Uso da concessão · <span className="mono">{profileName}</span>
        </div>
        <span style={{ flex: 1 }} />
        <label className="muted" style={{ fontSize: 13 }}>
          Janela{' '}
          <select value={days} onChange={(e) => changeWindow(Number(e.target.value))} aria-label="Janela de uso">
            <option value={1}>1 dia</option>
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
          </select>
        </label>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
          Fechar
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        Toda leitura do uso fica na auditoria. A loja aparece só como id curto; argumentos e respostas não são guardados.
      </p>
      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
      {loading && <p className="muted">Carregando…</p>}
      {!loading && !error && items.length === 0 && <p className="muted">Nenhuma chamada na janela.</p>}
      {items.length > 0 && (
        <div className="table-scroll">
          <table aria-label="Chamadas de ferramenta">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Ferramenta</th>
                <th>Desfecho</th>
                <th>Duração</th>
                <th>Loja</th>
                <th>Cliente</th>
              </tr>
            </thead>
            <tbody>
              {items.map((call) => (
                <tr key={call.id}>
                  <td>{formatDateTime(call.calledAt)}</td>
                  <td className="mono">{call.toolName}</td>
                  <td>
                    {call.outcome}
                    {call.errorCode ? ` (${call.errorCode})` : ''}
                  </td>
                  <td>{call.durationMs} ms</td>
                  <td className="mono">{call.targetTenantShortId ?? '—'}</td>
                  <td>
                    <span className="muted" style={{ fontSize: 12 }}>
                      declarado pelo cliente:
                    </span>{' '}
                    <span className="mono" data-testid="client-user-agent">
                      {call.clientUserAgent}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor && !loading && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          style={{ marginTop: 10 }}
          disabled={loadingMore}
          onClick={() => void loadMore()}
        >
          {loadingMore ? 'Carregando…' : 'Carregar mais'}
        </button>
      )}
    </section>
  );
}
